import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CapacityRebalanceDialog } from "@/components/campaigns/CapacityRebalanceDialog";
import type { CapacityBlock } from "@/lib/campaignHelpers";

interface CapacityResult {
  success: boolean;
  reason?: string;
  block?: CapacityBlock;
  error?: string;
}

/**
 * Shared handling for "this account has no daily capacity left" outcomes.
 *
 * Call `handleCapacityResult(result, campaignId)` inside a mutationFn: when the
 * action was blocked it opens the rebalance dialog so the user can free up
 * capacity instead of hitting a dead end, and returns true.
 */
export function useCapacityBlock() {
  const queryClient = useQueryClient();
  const [block, setBlock] = useState<CapacityBlock | null>(null);
  const [focusCampaignId, setFocusCampaignId] = useState<string | null>(null);

  const handleCapacityResult = (result: CapacityResult, campaignId?: string | null): boolean => {
    if (!result.success && result.reason === "capacity" && result.block) {
      setBlock(result.block);
      setFocusCampaignId(campaignId ?? null);
      return true;
    }
    return false;
  };

  const capacityDialog = block ? (
    <CapacityRebalanceDialog
      open
      onOpenChange={(open) => {
        if (!open) setBlock(null);
      }}
      clientId={block.clientId}
      focusCampaignId={focusCampaignId}
      onRebalanced={() => {
        queryClient.invalidateQueries({ queryKey: ["account-capacity"] });
        queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        setBlock(null);
      }}
    />
  ) : null;

  return { handleCapacityResult, capacityDialog };
}
